/**
 * Vertex Simulator - Interfaz simplificada para Gemini + RAG
 * ACCESO RESTRINGIDO: Solo davidprudencio@hotmail.com
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // 🎓 TICKET JPRXH6
import { useAuth } from '../../context/AuthContext';
// 🎫 TDZ-FIX: Import Sparkles con alias para evitar conflicto TDZ
import { MessageSquare, Loader2, Sparkles as SparklesIcon, Brain, Target, ArrowRight, BarChart3 } from 'lucide-react';
import VertexChatWindow from './components/VertexChatWindow';
import EvaluationReport from './components/EvaluationReport';
import { getSafeToken } from '../../utils/tokenHelper';
import { VERTEX_API_BASE } from '../../utils/vertexApiHelper';
import safeFetchJson from '../../utils/safeFetchJson'; // 🎟️ V4-P0-007

const VertexSimulator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams(); // 🎓 TICKET JPRXH6
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationSessionId, setEvaluationSessionId] = useState(null);
  const [isGeneratingEvaluation, setIsGeneratingEvaluation] = useState(false);
  
  // Ref para evitar llamadas duplicadas a /session/end
  const isEndingSession = useRef(false);
  
  // 🔒 Ref para evitar loop de auto-start después de cancelar onboarding
  const hasUserCancelledAutoStart = useRef(false);

  // 🔒 CONTROL DE ACCESO: Admin o usuarios con agentes asignados
  useEffect(() => {
    const isAdmin = user?.email === 'davidprudencio@hotmail.com';
    const hasAssignedAgents = user?.assignedAgents && user.assignedAgents.length > 0;
    
    if (!isAdmin && !hasAssignedAgents) {
      console.warn('⚠️ Usuario sin agentes asignados, redirigiendo a dashboard');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Cargar agentes disponibles
  useEffect(() => {
    const isAdmin = user?.email === 'davidprudencio@hotmail.com';
    const hasAssignedAgents = user?.assignedAgents && user.assignedAgents.length > 0;
    
    if (isAdmin || hasAssignedAgents) {
      fetchAgents();
    }
  }, [user]);

  // 🎓 TICKET JPRXH6 + NBRKT8 - Auto-iniciar sesión si viene agentId por query param
  useEffect(() => {
    // NBRKT8: Priorizar 'agentId', fallback a 'agent' (legacy JPRXH6)
    const agentIdFromQuery = searchParams.get('agentId') || searchParams.get('agent');
    const fromCourseId = searchParams.get('fromCourseId') || searchParams.get('fromCourse'); // Legacy
    const fromActivityId = searchParams.get('fromActivityId');
    
    // 🔒 FIX LOOP: No auto-start si el usuario canceló el onboarding
    if (hasUserCancelledAutoStart.current) {
      console.log('⚠️ [NBRKT8] Auto-start bloqueado - Usuario canceló el onboarding');
      return;
    }
    
    if (agentIdFromQuery && agents.length > 0 && !selectedAgent && !session) {
      console.log('🎓 [NBRKT8] Auto-iniciando sesión desde curso:', { 
        agentId: agentIdFromQuery, 
        fromCourseId, 
        fromActivityId 
      });
      
      // Buscar agente por agentId
      const targetAgent = agents.find(a => a.agentId === agentIdFromQuery);
      
      if (targetAgent) {
        console.log('✅ [NBRKT8] Agente encontrado, iniciando sesión:', targetAgent.nombreVisible);
        handleStartSession(targetAgent);
      } else {
        console.warn('⚠️ [NBRKT8] Agente no encontrado en lista disponible:', agentIdFromQuery);
        setError(`Agente "${agentIdFromQuery}" no disponible para tu usuario. Selecciona otro agente del catálogo.`);
      }
    }
  }, [agents, searchParams, selectedAgent, session]);

  const fetchAgents = async () => {
    try {
      const token = getSafeToken();
      if (!token) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }

      // 🎟️ V4-P0-007: Usar safeFetchJson con manejo robusto de errores
      const result = await safeFetchJson(`${VERTEX_API_BASE}/v4/agents`, {
        method: 'GET',
        token,
        verbose: true
      });

      if (!result.ok) {
        console.error('❌ [V4-P0-007] Error al cargar agentes:', {
          status: result.status,
          error: result.error,
          correlationId: result.correlationId
        });
        setError(`Error al cargar agentes: ${result.error || 'Desconocido'} (ID: ${result.correlationId || 'N/A'})`);
        setLoading(false);
        return;
      }
      
      const data = result.data;
      if (data.success) {
        console.log('✅ Agentes cargados:', data.agents.length);
        setAgents(data.agents);
      } else {
        console.error('❌ Error en response:', data);
        setError(`Error al cargar agentes: ${data.message || 'Desconocido'}`);
      }
    } catch (err) {
      console.error('❌ Exception:', err);
      setError(`Error inesperado: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentDetails = async (agentId, token) => {
    const response = await fetch(`${VERTEX_API_BASE}/v4/agents/${agentId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (!data?.success || !data?.agent) {
      throw new Error(data?.message || 'No se pudo obtener detalle del agente');
    }
    return data.agent;
  };

  const handleStartSession = async (agent) => {
    try {
      setLoading(true);
      const token = getSafeToken();
      
      if (!token) {
        setError('Usuario no autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }

      // MOTOR V4: Usar endpoints V4 operacionales (V3 está deprecated según TCK-V4-CONTRACT-001)
      const isV4Agent = agent.coachConfig && agent.coachConfig.enabled;

      // ✅ Ticket botón verde (FE): para V4, asegurar agent config completa (incluye evaluationCriteria)
      let resolvedAgent = agent;
      if (isV4Agent) {
        try {
          resolvedAgent = await fetchAgentDetails(agent.agentId, token);
        } catch (e) {
          console.warn('[VertexSimulator] ⚠️ No se pudo cargar detalle del agente, usando lista:', e?.message || e);
          resolvedAgent = agent;
        }
      }

      const endpoint = isV4Agent 
        ? `${VERTEX_API_BASE}/v4/session/start`
        : `${VERTEX_API_BASE}/chat/start`;

      console.log(`[VertexSimulator] Iniciando sesión - Agente ${isV4Agent ? 'V4' : 'V2'}: ${agent.agentId}`);

      // 🔍 DEBUG: Verificar userName que se enviará
      const rawUserName = user?.nombre || user?.name || user?.email?.split('@')[0] || '';
      const firstNameToken = String(rawUserName).trim().split(/\s+/)[0] || '';
      const firstNameSafe = firstNameToken.split(/[._-]/)[0] || '';
      const userName = firstNameSafe
        ? firstNameSafe.charAt(0).toUpperCase() + firstNameSafe.slice(1)
        : '';

      console.log('🔍 [VertexSimulator] userName a enviar (primer nombre):', userName);
      console.log('🔍 [VertexSimulator] user object:', { nombre: user?.nombre, name: user?.name, email: user?.email });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          agentId: resolvedAgent.agentId,
          // ✅ Enviar nombre del usuario para personalización
          userName,
          // V3: Opcionalmente pasar scenarioId si existe
          ...(isV4Agent && resolvedAgent.scenarioTemplates?.[0] && {
            scenarioId: resolvedAgent.scenarioTemplates[0].id
          })
        })
      });

      const data = await response.json();
      
      if (isV4Agent) {
        // Motor V4: Respuesta incluye sessionId + onboarding + phase + briefing + messages
        if (data.sessionId && data.onboarding) {
          setSelectedAgent(resolvedAgent);
          
          // 🎟️ P0-INFERENCIA-ONBOARDING-ESTRICTA: Frontend infiere fase UI con guardrails anti-frágiles
          // Regla estricta del experto:
          // - Si completedTurns === 0 Y messages.length === 0 → onboarding
          // - Si completedTurns === 0 PERO messages.length > 0 → NO onboarding (sesión con historial/semilla)
          // - Si completedTurns > 0 → NUNCA onboarding
          const completedTurns = data.completedTurns ?? 0;
          const messagesLength = (data.messages || []).length;
          const isOnboardingPhase = (completedTurns === 0) && (messagesLength === 0);
          
          console.log(`[VertexSimulator] 🔍 Inferencia onboarding: completedTurns=${completedTurns}, messages.length=${messagesLength}, isOnboarding=${isOnboardingPhase}`);
          
          setSession({
            sessionId: data.sessionId,
            onboarding: data.onboarding, // { title, message, consent }
            briefing: data.briefing, // 🎟️ P0-BRIEF-RENDER: Mensaje de brief para renderizar
            kickoff: data.kickoff, // Mensaje de kickoff si existe
            messages: data.messages || [], // Array de mensajes iniciales
            // 🎟️ P0-FASE-UI-DERIVADA: Calcular fase UI local (no persistir, no enviar al backend)
            phase: isOnboardingPhase ? 'onboarding' : 'simulation_active',
            // 🎟️ P0-METRICS-INIT: Capturar métricas iniciales para frontend
            completedTurns: completedTurns,
            aiTurnCount: data.aiTurnCount || 0,
            maxTurns: data.maxTurns || 8,
            currentPhase: data.currentPhase,  // Fase técnica del motor (EXPLORACION_INICIAL)
            currentPhaseDisplayName: data.currentPhaseDisplayName,
            phaseHistory: data.phaseHistory || [],
            progressPercentage: data.progressPercentage || 0,
            stateMachine: data.state || {},
            isV3: false,
            isV4: true
          });
          console.log(`[VertexSimulator V4] Sesión iniciada - Motor phase=${data.currentPhase}, UI phase=${isOnboardingPhase ? 'onboarding' : 'simulation_active'}`);
        } else {
          setError(data.error || 'Error al iniciar sesión V4');
        }
      } else {
        // Motor V2: Respuesta legacy
        if (data.success) {
          setSelectedAgent(agent);
          setSession({
            sessionId: data.sessionId,
            initialMessage: `Hola, soy ${data.agentInfo.nombreVisible}. ${data.agentInfo.descripcion}`,
            isV3: false
          });
        } else {
          setError(data.message || 'Error al iniciar sesión');
        }
      }
    } catch (err) {
      console.error('Error starting session:', err);
      setError('Error al iniciar chat');
    } finally {
      setLoading(false);
    }
  };

  // Acción canónica del botón verde (V4): SOLO /session/close
  const handleGenerateEvaluation = async (sessionId) => {
    try {
      setIsGeneratingEvaluation(true);
      const token = getSafeToken();

      const response = await fetch(`${VERTEX_API_BASE}/v4/session/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId })
      });

      const data = await response.json();
      if (!data.success) {
        console.error('[VertexSimulator] ❌ Error al generar evaluación:', data.error || data.message);
        alert('Error al generar evaluación: ' + (data.error || data.message || 'Desconocido'));
        return;
      }

      setEvaluationSessionId(sessionId);
      setShowEvaluation(true);
    } catch (error) {
      console.error('[VertexSimulator] ❌ Error:', error);
      alert('Error al procesar evaluación');
    } finally {
      setIsGeneratingEvaluation(false);
    }
  };

  const handleExit = async () => {
    // Evitar llamadas duplicadas
    if (isEndingSession.current) {
      console.log('[VertexSimulator] Ya se está finalizando la sesión, ignorando clic duplicado');
      return;
    }

    // Importante (P1): VertexChatWindow.exitSession() es el flujo canónico para /cancel.
    // Aquí solo hacemos cleanup local y navegación.
    isEndingSession.current = true;

    // 🔒 FIX LOOP: Marcar que el usuario salió para evitar auto-start repetido
    hasUserCancelledAutoStart.current = true;
    console.log('🔒 [VertexSimulator] Auto-start bloqueado tras salir de sesión');

    // 🎓 NBRKT8: Si viene de un curso, regresar al curso
    const fromCourseId = searchParams.get('fromCourseId') || searchParams.get('fromCourse');
    
    if (fromCourseId) {
      console.log('🎓 [NBRKT8] Regresando al curso:', fromCourseId);
      navigate(`/courses/${fromCourseId}`);
    } else {
      // Limpiar sesión y volver a lista de agentes
      setSelectedAgent(null);
      setSession(null);
    }

    isEndingSession.current = false;
  };

  const handleViewEvaluation = async (sessionId) => {
    try {
      setIsGeneratingEvaluation(true); // ✅ Mostrar loading
      console.log('[VertexSimulator] 🎯 Verificando evaluación...');
      
      const token = getSafeToken();
      
      // PASO 1: Verificar si ya existe evaluación generada
      const checkResponse = await fetch(`${VERTEX_API_BASE}/v4/session/${sessionId}/evaluation`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const checkData = await checkResponse.json();
      
      // Si la evaluación es válida, mostrar directamente
      const hasValidEvaluation = checkData.success 
        && checkData.evaluation?.overallScore != null 
        && checkData.evaluation?.overallScore > 0;
      
      if (!hasValidEvaluation) {
        console.log('[VertexSimulator] ⚠️ Evaluación no encontrada, generando...');
        
        // PASO 2: Generar evaluación si no existe
        const response = await fetch(`${VERTEX_API_BASE}/v4/session/close`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sessionId })
        });

        const data = await response.json();
        
        if (!data.success) {
          console.error('[VertexSimulator] ❌ Error al generar evaluación:', data.error);
          alert('Error al generar evaluación: ' + data.error);
          setIsGeneratingEvaluation(false);
          return;
        }

        console.log('[VertexSimulator] ✅ Evaluación generada exitosamente');
      } else {
        console.log('[VertexSimulator] ✅ Evaluación ya existe, mostrando...');
      }
      
      // PASO 3: Mostrar el reporte
      setEvaluationSessionId(sessionId);
      setShowEvaluation(true);
      setIsGeneratingEvaluation(false);
      
    } catch (error) {
      console.error('[VertexSimulator] ❌ Error:', error);
      alert('Error al procesar evaluación');
      setIsGeneratingEvaluation(false);
    }
  };

  const handleCloseEvaluation = () => {
    setShowEvaluation(false);
    setEvaluationSessionId(null);
    // Limpiar sesión sin llamar a /session/end (ya fue finalizada)
    setSelectedAgent(null);
    setSession(null);
  };

  // Confirmar onboarding y obtener briefing
  const handleConfirmOnboarding = async () => {
    console.log('[CONFIRM] handleConfirmOnboarding EXECUTED');
    if (!session?.sessionId) {
      console.error('[VertexSimulator] No hay sessionId para confirmar');
      return;
    }

    try {
      console.log('[VertexSimulator] Confirmando onboarding...');
      
      const token = getSafeToken();
      const response = await fetch(`${VERTEX_API_BASE}/v4/session/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          sessionId: session.sessionId
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error al confirmar sesión');
      }

      console.log('[VertexSimulator] Sesión confirmada, briefing recibido:', data);
      console.log('[VertexSimulator] 🔍 DEBUG data.briefing:', data.briefing);
      console.log('[VertexSimulator] 🔍 DEBUG data.brief:', data.brief);
      console.log('[VertexSimulator] 🔍 DEBUG typeof data.briefing:', typeof data.briefing);
      
      // ✅ TCK-P0-BRIEF-CONTRACT-FIX-V4: Backend retorna 'briefing' para UI
      const briefingData = data.briefing || null;
      console.log('[VertexSimulator] 🔍 DEBUG briefingData final:', briefingData);

      // 🎟️ TCK-P1-V4-BRIEF-KICKOFF-UX-POLISH-AND-CONTRACT
      // Kickoff NO se arma en frontend: sólo se renderiza si viene del backend como mensaje
      // con metadata.type === SYSTEM_KICKOFF y payload.source === pre_roll.
      const findKickoffFromBackend = (payload) => {
        if (!payload) return null;

        const isValidKickoff = (m) => {
          if (m?.metadata?.type !== 'SYSTEM_KICKOFF') return false;
          const source = m?.payload?.source;
          // Aceptar source ausente (fallback defensivo), pero NO aceptar source distinto
          if (source && source !== 'pre_roll') return false;
          return true;
        };

        // 1) data.kickoff (objeto)
        const direct = payload?.kickoff;
        if (isValidKickoff(direct)) return direct;

        // 2) data.messages (array)
        const msgs = Array.isArray(payload?.messages) ? payload.messages : [];
        const fromMessages = msgs.find(isValidKickoff);
        if (fromMessages) return fromMessages;

        return null;
      };

      const normalizeKickoffMessage = (raw) => {
        if (!raw) return null;
        const content = typeof raw.content === 'string' ? raw.content : (typeof raw.text === 'string' ? raw.text : '');
        const normalizedContent = String(content || '').trim();
        if (!normalizedContent) return null;

        const timestampRaw = raw.timestamp || raw.createdAt || null;
        return {
          ...raw,
          id: raw.id || raw._id || `kickoff-${session.sessionId}`,
          role: 'agent',
          speakerRole: 'CLIENT',
          content: normalizedContent,
          timestamp: timestampRaw ? new Date(timestampRaw) : new Date(),
          metadata: { ...(raw.metadata || {}), type: 'SYSTEM_KICKOFF' },
          payload: raw.payload
        };
      };

      const kickoffFromBackend = normalizeKickoffMessage(findKickoffFromBackend(data));
      const initialMessages = kickoffFromBackend ? [kickoffFromBackend] : [];
      
      setSession(prev => {
        const next = { ...prev, phase: 'simulation_active', briefing: briefingData, initialMessages };
        console.log('[SIMULATOR] next.phase', next.phase, 'hasBriefing', Boolean(next.briefing));
        return next;
      });
      
    } catch (error) {
      console.error('Error confirmando onboarding:', error);
      alert('Error al iniciar simulación. Intenta de nuevo.');
    }
  };

  // Cancelar onboarding sin finalizar sesión (no genera evaluación)
  const handleCancelOnboarding = async () => {
    console.log('[VertexSimulator] Cancelando onboarding');

    // P1: NO enviar /cancel durante pre-roll/onboarding sin interacción real.
    // El backend Variante A cierra siempre, así que aquí solo hacemos cleanup local.
    
    // 🔒 FIX LOOP: Marcar que el usuario canceló para evitar auto-start repetido
    hasUserCancelledAutoStart.current = true;
    console.log('🔒 [VertexSimulator] Auto-start bloqueado para evitar loop');
    
    // 🎓 NBRKT8: Si viene de un curso, regresar al curso
    const fromCourseId = searchParams.get('fromCourseId') || searchParams.get('fromCourse');
    
    if (fromCourseId) {
      console.log('🎓 [NBRKT8] Regresando al curso:', fromCourseId);
      navigate(`/courses/${fromCourseId}`);
    } else {
      // Limpiar sesión y volver a lista de agentes
      setSelectedAgent(null);
      setSession(null);
    }
    
    isEndingSession.current = false; // Resetear flag
  };

  // Si está mostrando evaluación
  if (showEvaluation && evaluationSessionId) {
    return (
      <EvaluationReport 
        sessionId={evaluationSessionId}
        onClose={handleCloseEvaluation}
      />
    );
  }

  // Si hay sesión activa, mostrar VertexChatWindow
  if (session && selectedAgent) {
    console.log('[SIMULATOR] passing session to chat', session?.phase, Boolean(session?.briefing));

    // Ticket botón verde evaluación (FE): NO recortar evaluationCriteria
    const agentForChat = {
      ...selectedAgent,
      agentId: selectedAgent.agentId,
      nombre: selectedAgent.nombreVisible,
      nombreVisible: selectedAgent.nombreVisible,
      avatar: selectedAgent.avatar || '🤖',
      descripcion: selectedAgent.descripcion,
      stateMachine: selectedAgent.stateMachine,
      evaluationCriteria: selectedAgent.evaluationCriteria
    };
    
    return (
      <>
        <VertexChatWindow
          agent={agentForChat}
          session={session}
          apiEndpoint={`${VERTEX_API_BASE}/v4/session/turn`}
          onExit={handleExit}
          onCancelOnboarding={handleCancelOnboarding}
          confirmOnboarding={handleConfirmOnboarding}
          onGenerateEvaluation={handleGenerateEvaluation}
          onViewEvaluation={handleViewEvaluation}
        />

        {/* Modal de "Generando evaluación..." */}
        {isGeneratingEvaluation && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 text-center">
              <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Generando tu evaluación
              </h3>
              <p className="text-gray-600 mb-4">
                Estamos analizando tu desempeño con inteligencia artificial...
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Esto puede tardar 10-30 segundos
              </p>
            </div>
          </div>
        )}
      </>
    );
  }

  // Selector de agentes
  return (
    <div className="h-full bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #94a3b8;
        }
      `}</style>
      {/* Contenido - DashboardLayout ya tiene header azul */}
      <div className="h-full overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        
        {/* Título de página y Acciones */}
        <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
              <MessageSquare className="w-8 h-8 text-indigo-600" />
              Laboratorio de Simulaciones con IA
            </h1>
            <p className="text-gray-600 max-w-2xl">
              Explora simulaciones interactivas diseñadas para entrenar habilidades mediante escenarios conversacionales.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Potenciado por Google Gemini + RAG
            </p>
          </div>

          {/* Botón de Métricas / Historial */}
          <button 
            onClick={() => navigate('/vertex/analytics')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-700 font-medium hover:bg-gray-50 hover:border-indigo-200 hover:text-indigo-600 transition-all group self-start"
          >
            <BarChart3 className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors" />
            <span>Mis Métricas</span>
          </button>
        </div>
        
        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        )}

        {/* Grid de agentes */}
        {!loading && agents.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent) => {
              const isV4 = agent.coachConfig && agent.coachConfig.enabled;
              
              return (
                <div
                  key={agent._id}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-indigo-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer group flex flex-col h-full relative overflow-hidden"
                  onClick={() => handleStartSession(agent)}
                >
                  {/* Decorative background element */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${isV4 ? 'from-purple-50 to-fuchsia-50' : 'from-indigo-50 to-blue-50'} rounded-bl-full -mr-10 -mt-10 opacity-50 group-hover:scale-110 transition-transform duration-500`} />

                  <div className="relative z-10 flex-1">
                    <div className="flex items-start justify-between mb-5">
                      <div className="relative">
                        {agent.avatar && agent.avatar.startsWith('/') ? (
                          <img 
                            src={agent.avatar} 
                            alt={agent.nombreVisible} 
                            className="w-16 h-16 rounded-2xl object-cover shadow-sm border-2 border-white ring-1 ring-gray-100 group-hover:ring-indigo-100 transition-all"
                          />
                        ) : (
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl border-2 border-white ring-1 ring-gray-100 shadow-sm ${isV4 ? 'bg-purple-50 text-purple-600' : 'bg-indigo-50 text-indigo-600'}`}>
                            {agent.avatar || '🤖'}
                          </div>
                        )}
                      </div>
                      
                      {/* Badges */}
                      <div className="flex flex-col items-end gap-2">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 border border-gray-200">
                          <Brain className="w-3 h-3" />
                          Vertex AI
                        </span>
                        {isV4 && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700 border border-purple-200">
                            <SparklesIcon className="w-3 h-3" />
                            Coach V4
                          </span>
                        )}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors line-clamp-2 min-h-[3.5rem]">
                      {agent.nombreVisible}
                    </h3>
                    
                    <div className="h-40 overflow-y-auto pr-2 mb-4 custom-scrollbar">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {agent.descripcion}
                      </p>
                    </div>
                  </div>

                  {/* Footer / CTA */}
                  <div className="relative z-10 mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                       <Target className="w-3.5 h-3.5" />
                       <span>Simulación</span>
                    </div>
                    
                    <div className={`flex items-center gap-2 text-sm font-bold transition-colors duration-300 ${
                      isV4 ? 'text-purple-600 group-hover:text-purple-700' : 'text-indigo-600 group-hover:text-indigo-700'
                    }`}>
                      <span className="relative">
                        Entrenar
                        <span className={`absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-300 group-hover:w-full ${
                          isV4 ? 'bg-purple-600' : 'bg-indigo-600'
                        }`}></span>
                      </span>
                      <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Estado vacío */}
        {!loading && agents.length === 0 && (
          <div className="text-center py-20">
            <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No hay agentes disponibles
            </h3>
            <p className="text-gray-600">
              Los agentes se configuran desde el backend
            </p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default VertexSimulator;
